import type { ReviewComment } from '../components/ReviewComments'

/**
 * The edit request a writer's paragraph comments turn into, and its inverse.
 *
 * Mirrors planFeedback.ts, but for a generated story block instead of an outline. The request
 * text is the whole payload the edit agent receives (see server/agent/storyEditAgent.ts): the
 * passage and the comments, nothing else, so the model has nothing to rewrite from except what
 * the writer pointed at.
 */

const PREAMBLE =
  'Please revise the passage below based on the comments underneath it. Treat this as a targeted edit: change ONLY what each comment asks for, and reproduce every other sentence exactly as written — do not rephrase, reorder, expand, trim, or restyle any part of the passage the comments do not mention. Return the complete revised passage.'

const PASSAGE_MARKER = '\n\nCURRENT PASSAGE:\n'
const COMMENTS_MARKER = '\n\nCOMMENTS:\n'

export function splitStoryParagraphs(content: string): string[] {
  return content
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
}

export function buildStoryEditRequest(passage: string, comments: ReviewComment[]): string {
  const active = comments.filter((comment) => !comment.resolved)
  const parts = active
    .filter((comment) => comment.lineIndex !== -1)
    .map((comment) => `Paragraph: "${comment.lineText}"\nComment: "${comment.comment}"`)
  const overall = active.find((comment) => comment.lineIndex === -1)
  if (overall) parts.push(`OVERALL COMMENT:\n"${overall.comment}"`)
  return `${PREAMBLE}${PASSAGE_MARKER}${passage}${COMMENTS_MARKER}${parts.join('\n\n')}`
}

export interface ParsedStoryEditRequest {
  passage: string
  paragraphComments: Array<{ paragraph: string; comment: string }>
  overallComment: string | null
}

export function parseStoryEditRequest(content: string): ParsedStoryEditRequest | null {
  const passageStart = content.indexOf(PASSAGE_MARKER)
  const commentsStart = content.lastIndexOf(COMMENTS_MARKER)
  if (passageStart === -1 || commentsStart === -1 || commentsStart < passageStart) return null
  const passage = content.slice(passageStart + PASSAGE_MARKER.length, commentsStart)
  const feedback = content.slice(commentsStart + COMMENTS_MARKER.length)
  const paragraphComments: ParsedStoryEditRequest['paragraphComments'] = []
  let overallComment: string | null = null
  for (const part of feedback.split('\n\n')) {
    const paragraphMatch = part.match(/^Paragraph: "([\s\S]*)"\nComment: "([\s\S]*)"$/)
    if (paragraphMatch) {
      paragraphComments.push({ paragraph: paragraphMatch[1], comment: paragraphMatch[2] })
      continue
    }
    const overallMatch = part.match(/^OVERALL COMMENT:\n"([\s\S]*)"$/)
    if (overallMatch) overallComment = overallMatch[1]
  }
  return { passage, paragraphComments, overallComment }
}

/** Markdown for the conversation bubble that shows what the writer asked to change. */
export function formatStoryEditMarkdown(parsed: ParsedStoryEditRequest): string {
  const sections = parsed.paragraphComments.map(
    ({ paragraph, comment }) => `> ${paragraph.replace(/\n/g, '\n> ')}\n\n${comment}`
  )
  if (parsed.overallComment !== null) sections.push(`**Overall**\n\n${parsed.overallComment}`)
  return `### Requested passage changes\n\n${sections.join('\n\n')}`
}
